import { driver, type Config, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { courseApi } from '../../services/courseApi';

import './hostTour.css';
import { waitForSelector, nextFrame } from './readiness';
import {
  POST_SUBMIT_STEPS,
  findPage,
  postSubmitStep,
  findSectionByPath,
  firstStepIndexOnPage,
  followingSection,
  getSection,
  resolveStepIndex,
  visibleSteps,
} from './sections';
import { useHostTourStore } from './store';
import { tourSelector } from './targets';
import type { PostSubmit, RunKind, SectionId, TourPage, TourSection, TourStep } from './types';

const READY_TIMEOUT_MS = 6000;
const HOST_PREFIX = '/host/';

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

function isRealElement(el: Element | undefined): el is Element {
  return !!el && el.id !== 'driver-dummy-element';
}

/**
 * Owns the driver.js instance for the host walkthrough. Mount once inside the
 * router; it watches the route and the tour store and starts, resumes, or
 * tears down the overlay accordingly. Pages only need to render `data-tour`
 * anchors (see targets.ts).
 */
export function HostTourProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, getToken } = useAuth();

  const hydrate = useHostTourStore((s) => s.hydrate);
  const userId = useHostTourStore((s) => s.userId);
  const mode = useHostTourStore((s) => s.mode);
  const paused = useHostTourStore((s) => s.paused);
  const sections = useHostTourStore((s) => s.sections);
  const runSectionId = useHostTourStore((s) => s.activeRun?.sectionId);
  const runKind = useHostTourStore((s) => s.activeRun?.kind);
  const postSubmitKind = useHostTourStore((s) => s.postSubmit?.kind);
  const postSubmitSection = useHostTourStore((s) => s.postSubmit?.sectionId);

  const isHostRoute = pathname.startsWith(HOST_PREFIX);
  const canTour = !!user && !!(user.isHost || user.isAdmin);
  const isAdmin = !!user?.isAdmin;

  const driverRef = useRef<Driver | null>(null);
  const runIdRef = useRef(0);
  /** Click listeners on highlighted controls; removed on step change and on teardown. */
  const clickListenersRef = useRef<Array<{ el: Element; handler: EventListener }>>([]);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  /** Invalidates pending async starts and removes any overlay. */
  const destroyDriver = useCallback(() => {
    runIdRef.current += 1;
    for (const { el, handler } of clickListenersRef.current)
      el.removeEventListener('click', handler);
    clickListenersRef.current = [];
    const d = driverRef.current;
    driverRef.current = null;
    if (d) {
      try {
        d.destroy();
      } catch {
        // Already gone.
      }
    }
  }, []);

  /** Navigates for the tour, telling the route watcher which path to wait for. */
  const tourNavigate = useCallback((path: string) => {
    useHostTourStore.getState().beginNavigation(path);
    navigateRef.current(path);
  }, []);

  /**
   * After a section ends by Next or Skip, the run moves on to the following
   * section (see followingSection) and navigates to its page. Only closing
   * the popover stops the tour.
   */
  const advanceAfter = useCallback(
    (sectionId: SectionId, kind: RunKind) => {
      const st = useHostTourStore.getState();
      const next = followingSection(sectionId, kind, st.sections);
      if (!next) return;
      st.startRun(next.id, 0, kind);
      if (!findPage(next, pathnameRef.current)) tourNavigate(next.pages[0].entry);
    },
    [tourNavigate]
  );

  const buildStep = useCallback(
    (
      step: TourStep,
      index: number,
      sectionIndex: number,
      total: number,
      section: TourSection,
      kind: RunKind,
      onElementClick: (index: number, el: Element) => void,
      onSettled: (index: number) => void
    ): DriveStep => {
      const isLast = index === total - 1;
      let doneText = 'Finish';
      if (isLast && section.id !== 'finish') {
        const remaining = {
          ...useHostTourStore.getState().sections,
          [section.id]: 'done' as const,
        };
        const next = followingSection(section.id, kind, remaining);
        if (next && next.id !== 'finish') doneText = `Next: ${next.title}`;
      }
      let clickTarget: Element | null = null;
      const clickHandler: EventListener = (e) => {
        // Modified clicks (new tab, etc.) leave this tab where it is, so the tour stays too.
        const m = e as MouseEvent;
        if (m.button !== 0 || m.metaKey || m.ctrlKey || m.shiftKey || m.altKey) return;
        if (clickTarget) onElementClick(index, clickTarget);
      };
      const detach = () => {
        if (!clickTarget) return;
        clickTarget.removeEventListener('click', clickHandler);
        clickListenersRef.current = clickListenersRef.current.filter(
          (l) => l.handler !== clickHandler
        );
        clickTarget = null;
      };
      return {
        element: step.target ? tourSelector(step.target) : undefined,
        waitForElement: step.waitMs,
        popover: {
          title: step.title,
          description: step.body,
          side: step.side ?? 'bottom',
          align: step.align ?? 'center',
          showButtons: ['next', 'previous', 'close'],
          nextBtnText: step.nextLabel ?? 'Next',
          doneBtnText: isLast ? doneText : (step.nextLabel ?? 'Next'),
          prevBtnText: 'Back',
          progressText: `${section.title} · {{current}} of {{total}}`,
        },
        // The element is clickable as soon as the highlight starts (before the
        // 400 ms transition ends), so the listener goes on at that moment.
        onHighlightStarted: (el) => {
          if (step.advanceOnClick && isRealElement(el)) {
            clickTarget = el;
            el.addEventListener('click', clickHandler);
            clickListenersRef.current.push({ el, handler: clickHandler });
          }
        },
        onHighlighted: () => {
          useHostTourStore.getState().setStepIndex(sectionIndex);
          onSettled(index);
        },
        onDeselected: detach,
      };
    },
    []
  );

  const baseConfig = useCallback(
    (extraClass: string): Config => ({
      animate: !prefersReducedMotion(),
      allowClose: true,
      overlayOpacity: 0.55,
      stagePadding: 6,
      stageRadius: 10,
      popoverClass: `yoyo-tour ${extraClass}`.trim(),
      // Clicking the dimmed area does nothing: hosts keep working in the highlighted control.
      overlayClickBehavior: () => {},
      showProgress: true,
      disableActiveInteraction: false,
      allowKeyboardControl: true,
    }),
    []
  );

  const startSection = useCallback(
    async (
      section: TourSection,
      page: TourPage,
      stepIndex: number,
      kind: RunKind,
      runId: number
    ) => {
      if (page.readyTarget) await waitForSelector(tourSelector(page.readyTarget), READY_TIMEOUT_MS);
      await nextFrame();
      if (runIdRef.current !== runId) return;

      const pageOf = (key: string) => section.pages.find((p) => p.key === key) ?? page;

      // Only steps that can be shown here; indexes below are into this list,
      // while the store keeps the section-level index of the current step.
      const visible = visibleSteps(
        section,
        page.key,
        (target) => !!document.querySelector(tourSelector(target))
      );
      const firstVisible = visible.findIndex((v) => v.index >= stepIndex);
      const startAt = firstVisible === -1 ? visible.length - 1 : firstVisible;

      // `clickDestination`: the host finished the section by clicking a
      // highlighted link, which is already navigating there, so the tour must
      // not navigate too. When the next pending section lives on that page its
      // run is queued and the route watcher starts it on arrival; when it does
      // not, the host's own navigation wins and pending sections keep starting
      // on their pages (guided mode is page-driven).
      const complete = (status: 'done' | 'skipped', clickDestination?: string) => {
        destroyDriver();
        const st = useHostTourStore.getState();
        // A replay that is skipped keeps the section's earlier status.
        if (kind === 'replay' && status === 'skipped') st.endRun();
        else st.finishSection(section.id, status);
        if (clickDestination === undefined) {
          advanceAfter(section.id, kind);
          return;
        }
        const next = followingSection(section.id, kind, useHostTourStore.getState().sections);
        if (!next) return;
        const landing = findPage(next, clickDestination);
        if (!landing) return;
        useHostTourStore
          .getState()
          .startRun(next.id, firstStepIndexOnPage(next, landing.key), kind);
        useHostTourStore.getState().beginNavigation(clickDestination);
      };

      // driver.js runs a 400 ms highlight transition; starting another move
      // inside it leaves the previous element marked active. Moves therefore
      // wait until the current step has settled (its onHighlighted fired).
      let settledIndex = -1;
      let pendingMove: number | null = null;
      const inTransition = () => !!driverRef.current?.getState('__transitionCallback');
      const moveWhenSettled = (target: number) => {
        if (inTransition()) {
          pendingMove = target;
          return;
        }
        pendingMove = null;
        // Let the click's own effects (a modal opening, rows mounting) render first.
        window.setTimeout(() => driverRef.current?.moveTo(target), 50);
      };
      // Called from onHighlighted, which driver.js runs *before* it clears its
      // transition state, so a queued move is scheduled directly (the timeout
      // lands after the state is cleared) instead of re-checking inTransition.
      const onSettled = (index: number) => {
        settledIndex = index;
        if (pendingMove === null || pendingMove === index) return;
        const target = pendingMove;
        pendingMove = null;
        window.setTimeout(() => driverRef.current?.moveTo(target), 50);
      };

      const goTo = (target: number) => {
        const d = driverRef.current;
        if (!d) return;
        if (target < 0) return;
        if (inTransition()) return;
        if (target >= visible.length) {
          complete('done');
          return;
        }
        const nextStep = visible[target].step;
        if (nextStep.page !== page.key) {
          useHostTourStore.getState().setStepIndex(visible[target].index);
          destroyDriver();
          tourNavigate(pageOf(nextStep.page).entry);
          return;
        }
        d.moveTo(target);
      };

      const close = () => {
        if (!driverRef.current) return;
        destroyDriver();
        const st = useHostTourStore.getState();
        if (kind === 'guided') st.pause();
        else st.endRun();
      };

      // Clicking the highlighted control on an advanceOnClick step. When the
      // next step lives on another page the click itself navigates (it is a
      // link), so only the stored position moves; the route effect resumes there.
      const onElementClick = (index: number, el: Element) => {
        const target = index + 1;
        if (target >= visible.length) {
          const destination =
            el instanceof HTMLAnchorElement && el.pathname ? el.pathname : pathnameRef.current;
          complete('done', destination);
          return;
        }
        const nextStep = visible[target].step;
        if (nextStep.page !== page.key) {
          useHostTourStore.getState().setStepIndex(visible[target].index);
          destroyDriver();
          return;
        }
        if (settledIndex !== index) {
          pendingMove = target;
          return;
        }
        moveWhenSettled(target);
      };

      const steps = visible.map((v, i) =>
        buildStep(v.step, i, v.index, visible.length, section, kind, onElementClick, onSettled)
      );

      const d = driver({
        ...baseConfig(section.optional ? 'yoyo-tour-optional' : ''),
        steps,
        onNextClick: () => goTo((driverRef.current?.getActiveIndex() ?? 0) + 1),
        onPrevClick: () => goTo((driverRef.current?.getActiveIndex() ?? 0) - 1),
        onCloseClick: close,
        onDestroyStarted: close,
        onPopoverRender: (popover) => {
          if (section.id === 'finish') return;
          // The popover DOM is reused across steps; add the button once.
          if (popover.footerButtons.querySelector('.yoyo-tour-skip')) return;
          const skip = document.createElement('button');
          skip.type = 'button';
          skip.className = 'driver-popover-footer-btn yoyo-tour-skip';
          skip.textContent = 'Skip section';
          skip.addEventListener('click', () => complete('skipped'));
          popover.footerButtons.insertBefore(skip, popover.footerButtons.firstChild);
        },
      });
      driverRef.current = d;
      d.drive(startAt);
    },
    [advanceAfter, baseConfig, buildStep, destroyDriver, tourNavigate]
  );

  const startPostSubmit = useCallback(
    async (postSubmit: PostSubmit, runId: number) => {
      const spec = POST_SUBMIT_STEPS[postSubmit.sectionId];
      const step = postSubmitStep(spec, postSubmit.outcome);
      await waitForSelector(tourSelector(spec.readyTarget), READY_TIMEOUT_MS);
      await nextFrame();
      if (runIdRef.current !== runId) return;

      const finish = (chain: boolean) => {
        if (!driverRef.current) return;
        destroyDriver();
        useHostTourStore.getState().clearPostSubmit();
        if (chain) advanceAfter(postSubmit.sectionId, postSubmit.kind);
      };

      const d = driver({
        ...baseConfig(''),
        showProgress: false,
        steps: [
          {
            element: tourSelector(step.target as string),
            popover: {
              title: step.title,
              description: step.body,
              side: step.side,
              align: step.align,
              showButtons: ['next', 'close'],
              doneBtnText: step.nextLabel,
            },
          },
        ],
        onNextClick: () => finish(true),
        onCloseClick: () => finish(false),
        onDestroyStarted: () => finish(false),
      });
      driverRef.current = d;
      d.drive(0);
    },
    [advanceAfter, baseConfig, destroyDriver]
  );

  // Load this user's saved progress.
  useEffect(() => {
    if (user?.userId) hydrate(user.userId);
  }, [user?.userId, hydrate]);

  // First visit: hosts with no content yet get the guided run, everyone else
  // gets the Guide button only. Admins never auto-start. `pathname` is a
  // dependency on purpose: a failed probe is retried on the next host page.
  useEffect(() => {
    if (!isHostRoute || authLoading || !canTour || !userId || mode !== 'unknown') return;
    if (isAdmin) {
      useHostTourStore.getState().setMode('manual');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const [activitiesRes, courses] = await Promise.all([
          fetch(`${apiUrl}/host/activities`, { headers: { Authorization: `Bearer ${token}` } }),
          courseApi.hostListCourses(token),
        ]);
        if (!activitiesRes.ok) return;
        const activities = (await activitiesRes.json()) as { data?: unknown[] };
        const count = (activities.data?.length ?? 0) + courses.length;
        if (!cancelled) useHostTourStore.getState().setMode(count === 0 ? 'guided' : 'manual');
      } catch {
        // Leave the mode undecided; the next host page visit tries again.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isHostRoute, pathname, authLoading, canTour, userId, mode, isAdmin, getToken]);

  // Route + store watcher: decides what (if anything) is on screen.
  useEffect(() => {
    destroyDriver();
    if (!isHostRoute || authLoading || !canTour || !userId || mode === 'unknown') return;
    const runId = runIdRef.current;
    const st = useHostTourStore.getState();

    if (st.postSubmit && pathname === POST_SUBMIT_STEPS[st.postSubmit.sectionId].route) {
      void startPostSubmit(st.postSubmit, runId);
      return destroyDriver;
    }

    const run = st.activeRun;
    if (run) {
      if (run.navigatingTo) {
        // Mid-navigation: wait for the destination route before deciding anything.
        if (pathname !== run.navigatingTo) return destroyDriver;
        st.clearNavigation();
      }
      const section = getSection(run.sectionId);
      const page = findPage(section, pathname);
      if (!page) {
        // The host wandered off mid-section; drop the run. Guided sections stay
        // pending and start over when the host returns to their page.
        st.endRun();
        return destroyDriver;
      }
      if (run.kind === 'guided' && st.paused) return destroyDriver;
      void startSection(
        section,
        page,
        resolveStepIndex(section, run.stepIndex, page.key),
        run.kind,
        runId
      );
      return destroyDriver;
    }

    if (mode !== 'guided' || st.paused) return destroyDriver;
    const match = findSectionByPath(pathname);
    if (!match || st.sections[match.section.id] !== 'pending') return destroyDriver;
    st.startRun(match.section.id, firstStepIndexOnPage(match.section, match.page.key), 'guided');
    return destroyDriver;
  }, [
    pathname,
    isHostRoute,
    authLoading,
    canTour,
    userId,
    mode,
    paused,
    sections,
    runSectionId,
    runKind,
    postSubmitKind,
    postSubmitSection,
    destroyDriver,
    startSection,
    startPostSubmit,
  ]);

  return <>{children}</>;
}
